
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { convert24to12, convert12to24, validateTimeFormat, getTimeFormatPlaceholder } from '@/utils/timeUtils';
import { useIsMobile } from '@/hooks/use-mobile';

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  timeFormat: '12' | '24';
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  timeFormat,
  disabled = false,
  className,
  placeholder
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const isMobile = useIsMobile();

  // Convert stored 24-hour value to display format
  useEffect(() => {
    if (value) {
      if (isMobile) {
        // For mobile native time inputs, always use 24-hour format internally
        // but we show a display label for user feedback if needed
        setDisplayValue(value);
      } else {
        // For desktop, convert based on timeFormat setting
        const converted = timeFormat === '12' ? convert24to12(value) : value;
        setDisplayValue(converted);
      }
    } else {
      setDisplayValue('');
    }
  }, [value, timeFormat, isMobile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    
    if (isMobile) {
      // On mobile with native time input, value is in HH:MM format
      // We need to store it in 24-hour format regardless of display format
      setIsValid(true);
      onChange(inputValue); // Native time input always gives us HH:MM (24-hour format)
    } else {
      // On desktop, validate the input format
      const valid = validateTimeFormat(inputValue, timeFormat);
      setIsValid(valid);
      
      if (valid) {
        // Convert to 24-hour format for storage
        const normalized = timeFormat === '12' ? convert12to24(inputValue) : inputValue;
        onChange(normalized);
      }
    }
  };

  const handleBlur = () => {
    // Re-validate on blur and reset to last valid value if invalid
    // Skip validation for mobile native time inputs
    if (!isMobile && !isValid && value) {
      const converted = timeFormat === '12' ? convert24to12(value) : value;
      setDisplayValue(converted);
      setIsValid(true);
    }
  };

  const formatPlaceholder = placeholder || (isMobile ? '' : getTimeFormatPlaceholder(timeFormat));

  // For mobile with 12-hour format, show a helper display
  const showFormatHelper = isMobile && timeFormat === '12' && value;
  const formatHelperText = showFormatHelper ? convert24to12(value) : '';

  return (
    <div className="space-y-1">
      <Input
        type={isMobile ? "time" : "text"}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={formatPlaceholder}
        className={cn(
          className,
          !isValid && 'border-destructive focus:border-destructive'
        )}
      />
      {showFormatHelper && (
        <div className="text-xs text-muted-foreground px-1">
          {formatHelperText}
        </div>
      )}
    </div>
  );
};
